import { useState } from "react";

import {
  DateInput,
  GoogleMapAutoComplete,
  Input,
  Label,
  PhoneInput,
  TextArea,
} from "@web-app/components";
import { FieldValue, Order, OrderDeliveryType } from "@web-app/types";
import { isSameDate } from "@web-app/utils/time";

import AddressDetailsAccordion from "./AddressDetailsAccordion";
import DateChangeModal from "./DateChangeModal";

interface DropoffDetailsTabProps {
  debouncedHandleFieldChange: (property: string, value: FieldValue) => void;
  onFieldChange: (
    property: string,
    value: FieldValue,
    fromTab?: "dropoff" | "pickup",
  ) => void;
  order: Order;
}

export default function DropoffDetailsTab({
  order,
  onFieldChange,
  debouncedHandleFieldChange,
}: DropoffDetailsTabProps) {
  const isP2PDelivery =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const [newStartDate, setNewStartDate] = useState<Date | null>(null);

  // DateInput already converts the selected time from order's timezone to UTC,
  // so we just need to call toISOString() directly
  const onStartDateChange = (date: Date) => {
    onFieldChange("scheduledStartDateTime", date.toISOString(), "dropoff");
  };

  return (
    <div className="space-y-3 lg:space-y-4 px-4 pb-10 lg:pb-20">
      <div className="flex flex-col gap-1">
        <Label required>Full Name</Label>
        <Input
          defaultValue={order.dropoff?.name || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("dropoff.name", value)
          }
          placeholder="Enter drop-off name"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label required>Phone Number</Label>
        <PhoneInput
          defaultValue={order.dropoff?.phone || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("dropoff.phone", value)
          }
        />
      </div>

      <div className="flex flex-col gap-0">
        <div className="flex flex-col gap-1">
          <Label required>Address</Label>
          <GoogleMapAutoComplete
            // @ts-ignore - address is not a required field
            address={{ description: order.dropoff?.address || "" }}
            setAddress={async (address) => {
              await onFieldChange("dropoff.address", address);
            }}
            placeholder="Enter dropoff location"
          />
        </div>

        <AddressDetailsAccordion
          addressType="dropoff"
          debouncedHandleFieldChange={debouncedHandleFieldChange}
          location={order.dropoff}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {!isP2PDelivery && (
          <div className="flex flex-col gap-1">
            <Label required>Drop-off After (Date/Time)</Label>
            <DateInput
              value={new Date(order.scheduledStartDateTime)}
              onChange={(date) => {
                if (isSameDate(date, order.scheduledStartDateTime)) {
                  onStartDateChange(date);
                } else {
                  setNewStartDate(date);
                }
              }}
              timezone={order.timezone}
              position="under"
            />
          </div>
        )}

        {newStartDate && (
          <DateChangeModal
            startDate={newStartDate}
            orderTimezone={order.timezone}
            onDateChangeCancel={() => setNewStartDate(null)}
            onDateChangeConfirm={() => {
              onStartDateChange(newStartDate);
              setNewStartDate(null);
            }}
          />
        )}

        <div className="flex flex-col gap-1">
          <Label required>
            {isP2PDelivery
              ? "Drop-off At (End Time)"
              : "Drop-off Before (Date/Time)"}
          </Label>
          <DateInput
            value={new Date(order.scheduledEndDateTime)}
            onChange={(date) =>
              onFieldChange(
                "scheduledEndDateTime",
                date.toISOString(),
                "dropoff",
              )
            }
            timezone={order.timezone}
            position="under"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Label>Business Name</Label>
        <Input
          defaultValue={order.dropoff?.businessName || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("dropoff.businessName", value)
          }
          placeholder="Enter business name"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Note</Label>
        <TextArea
          placeholder="Enter drop-off note"
          rows={3}
          value={order.dropoff.note}
          onConfirm={(value) => {
            onFieldChange("dropoff.note", value);
          }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Order Requirements</Label>
        <Input
          type="checkbox"
          rightLabel="Signature Required"
          value={order.dropoffRequirements.signature ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("dropoffRequirements.signature", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Photo Required"
          value={order.dropoffRequirements.photo ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("dropoffRequirements.photo", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Notes Required"
          value={order.dropoffRequirements.note ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("dropoffRequirements.note", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Item Checklist Required"
          value={order.dropoffRequirements.itemChecklist ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("dropoffRequirements.itemChecklist", value === "true")
          }
        />
      </div>
    </div>
  );
}
